import { detectKakaoBrowser } from './kakao-browser';

export type FortuneSaveMode = 'download' | 'preview';

export interface FortuneSavePlatform {
  readonly userAgent: string;
  readonly maxTouchPoints?: number;
  readonly readDataUrl: (file: File) => Promise<string>;
  readonly preview: (file: File, dataUrl: string) => void;
  readonly download: (file: File) => void;
}

export function needsFortuneImagePreview(userAgent: string, maxTouchPoints = 0): boolean {
  return detectKakaoBrowser(userAgent).isKakao
    || /iPhone|iPad|iPod/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1);
}

export function imageFileDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string' && reader.result.startsWith('data:image/')) {
        resolve(reader.result);
      } else {
        reject(new Error('저장할 이미지를 준비하지 못했어요.'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('이미지를 읽지 못했어요.'));
    reader.onabort = () => reject(new Error('이미지 준비가 중단됐어요. 다시 시도해 주세요.'));
    reader.readAsDataURL(file);
  });
}

export function canShareFortuneImage(file: File): boolean {
  try {
    return typeof navigator.share === 'function'
      && typeof navigator.canShare === 'function'
      && navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

export async function sharePreparedFortuneImage(file: File): Promise<'shared' | 'cancelled' | 'unavailable'> {
  if (!canShareFortuneImage(file)) return 'unavailable';
  try {
    // The file is ready before the tap; call share before any asynchronous work.
    await navigator.share({ files: [file], title: '오늘의 한입 운세' });
    return 'shared';
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
    return 'unavailable';
  }
}

function showImagePreview(file: File, dataUrl: string, returnFocusTo?: HTMLElement): void {
  document.querySelector<HTMLDialogElement>('#fortune-save-dialog')?.close();
  const dialog = document.createElement('dialog');
  dialog.id = 'fortune-save-dialog';
  dialog.className = 'fortune-save-dialog';
  dialog.setAttribute('aria-labelledby', 'fortune-save-title');
  dialog.setAttribute('aria-describedby', 'fortune-save-description');
  dialog.innerHTML = `
    <div class="fortune-save-heading">
      <h2 id="fortune-save-title">운세 이미지 저장</h2>
      <button class="fortune-save-close" type="button" aria-label="이미지 저장 닫기" autofocus>×</button>
    </div>
    <p id="fortune-save-description">이미지를 길게 눌러 저장하거나 아래 버튼을 이용해 주세요.</p>
    <img class="fortune-save-image" alt="오늘의 한입 운세 카드" width="1080" height="1080" />
    <div class="fortune-save-buttons">
      <button class="button button-primary" type="button" data-native-share hidden>공유 메뉴로 저장</button>
      <a class="button button-soft" data-image-download>이미지 다운로드</a>
    </div>
    <p class="fortune-save-status" role="status" aria-live="polite"></p>
    <p class="fortune-save-help">다운로드한 이미지는 사진 앱 대신 파일 앱에 저장될 수 있어요.</p>`;

  dialog.querySelector<HTMLImageElement>('img')!.src = dataUrl;
  const download = dialog.querySelector<HTMLAnchorElement>('[data-image-download]')!;
  download.href = dataUrl;
  download.download = file.name;
  const status = dialog.querySelector<HTMLElement>('.fortune-save-status')!;
  download.addEventListener('click', () => {
    status.textContent = '다운로드를 요청했어요. 저장되지 않으면 이미지를 길게 눌러 보세요.';
  });

  const share = dialog.querySelector<HTMLButtonElement>('[data-native-share]')!;
  share.hidden = !canShareFortuneImage(file);
  share.addEventListener('click', async () => {
    share.disabled = true;
    const mode = await sharePreparedFortuneImage(file);
    share.disabled = false;
    if (mode === 'unavailable') {
      status.textContent = '공유 메뉴를 열지 못했어요. 이미지를 길게 누르거나 이미지 다운로드를 이용해 주세요.';
    } else if (mode === 'shared') {
      status.textContent = '공유 메뉴를 닫았어요. 사진 앱이나 선택한 저장 위치를 확인해 주세요.';
    } else {
      status.textContent = '';
    }
  });

  if (detectKakaoBrowser(navigator.userAgent).isKakao) {
    const help = document.createElement('p');
    help.className = 'fortune-save-help';
    help.textContent = '저장 메뉴가 열리지 않으면 카카오톡 브라우저 메뉴에서 다른 브라우저로 열어 다시 시도해 주세요.';
    dialog.append(help);
  }

  dialog.querySelector('button.fortune-save-close')!.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right
      || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    dialog.remove();
    if (!document.querySelector('#fortune-save-dialog[open]')) {
      document.documentElement.classList.remove('fortune-save-open');
      if (returnFocusTo?.isConnected) returnFocusTo.focus({ preventScroll: true });
    }
  }, { once: true });
  document.body.append(dialog);
  try {
    dialog.showModal();
  } catch (error) {
    dialog.remove();
    throw error;
  }
  document.documentElement.classList.add('fortune-save-open');
}

function downloadImage(file: File): void {
  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function saveFortuneImage(
  file: File,
  returnFocusTo?: HTMLElement,
  platform: FortuneSavePlatform = {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    readDataUrl: imageFileDataUrl,
    preview: (image, dataUrl) => showImagePreview(image, dataUrl, returnFocusTo),
    download: downloadImage,
  },
): Promise<FortuneSaveMode> {
  if (needsFortuneImagePreview(platform.userAgent, platform.maxTouchPoints)) {
    // Keep a real image and a persistent data-URL link available for a direct tap.
    // Kakao's iOS WebView may silently ignore blob-URL downloads.
    const dataUrl = await platform.readDataUrl(file);
    platform.preview(file, dataUrl);
    return 'preview';
  }
  platform.download(file);
  return 'download';
}
